import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import {
  ageFromBirthDate,
  createStudent,
  CreateStudentInput,
  getStudents,
  newIdempotencyKey,
  STUDENT_LIMITS,
} from '../api/students';
import { switchStudent } from '../../../core/identity-access/store/contextSlice';
import { AppDispatch } from '../../../store/store';

interface FormState {
  firstName: string;
  lastName: string;
  birthDate: string;
  grade: string;
}

type Errors = Partial<Record<keyof FormState | 'limit', string>>;

/**
 * Register a child under the signed-in parent.
 *
 * `POST /students` has shipped the whole time with nothing calling it, so a
 * parent with no students had no way forward at all: the switcher said "No hay
 * alumnos registrados" and offered no action, and every enrollment flow needs a
 * child to enrol.
 */
const AddStudent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    birthDate: '',
    grade: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [existingCount, setExistingCount] = useState<number | null>(null);

  // PRT-08-FR-01 AC-03 / UC-P02 A7 — one key per learner this form is trying
  // to create. A retry of the same details resends it, so a create whose
  // response was lost comes back as the learner it made rather than a second
  // one. Editing the details after a failed attempt is a different learner,
  // and gets a new key: reusing the old one would be refused by the server as
  // a key spent on someone else.
  const attempt = useRef<{ key: string; details: string } | null>(null);

  // The API caps a parent at 5 children; say so before they fill the form.
  useEffect(() => {
    let cancelled = false;
    getStudents()
      .then((list) => {
        if (!cancelled) setExistingCount(list.length);
      })
      .catch(() => {
        if (!cancelled) setExistingCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const atLimit = existingCount !== null && existingCount >= STUDENT_LIMITS.maxPerParent;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
    setApiError('');
  };

  const validate = (): boolean => {
    const next: Errors = {};

    if (!form.firstName.trim()) next.firstName = 'Ingresa el nombre.';
    else if (form.firstName.trim().length > STUDENT_LIMITS.nameMaxLength) {
      next.firstName = `Máximo ${STUDENT_LIMITS.nameMaxLength} caracteres.`;
    }

    if (!form.lastName.trim()) next.lastName = 'Ingresa los apellidos.';
    else if (form.lastName.trim().length > STUDENT_LIMITS.nameMaxLength) {
      next.lastName = `Máximo ${STUDENT_LIMITS.nameMaxLength} caracteres.`;
    }

    if (!form.birthDate) {
      next.birthDate = 'Ingresa la fecha de nacimiento.';
    } else {
      const parsed = new Date(form.birthDate);
      if (Number.isNaN(parsed.getTime())) {
        next.birthDate = 'Ingresa una fecha válida.';
      } else if (parsed > new Date()) {
        next.birthDate = 'La fecha no puede estar en el futuro.';
      } else {
        // Mirrors the server rule so the parent is not bounced by a 400.
        const age = ageFromBirthDate(form.birthDate);
        if (age < STUDENT_LIMITS.minAge || age > STUDENT_LIMITS.maxAge) {
          next.birthDate = `IQMaxx es para alumnos de ${STUDENT_LIMITS.minAge} a ${STUDENT_LIMITS.maxAge} años. Con esa fecha tendría ${age}.`;
        }
      }
    }

    const grade = Number(form.grade);
    if (!form.grade) {
      next.grade = 'Elige el grado.';
    } else if (
      !Number.isInteger(grade) ||
      grade < STUDENT_LIMITS.minGrade ||
      grade > STUDENT_LIMITS.maxGrade
    ) {
      next.grade = `Entre ${STUDENT_LIMITS.minGrade} y ${STUDENT_LIMITS.maxGrade}.`;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (atLimit || !validate()) return;

    setSaving(true);
    setApiError('');
    const details: CreateStudentInput = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      birthDate: form.birthDate,
      grade: Number(form.grade),
    };
    const signature = JSON.stringify(details);
    if (attempt.current?.details !== signature) {
      attempt.current = { key: newIdempotencyKey(), details: signature };
    }
    try {
      const student = await createStudent({ ...details, idempotencyKey: attempt.current.key });
      // Make the child they just added the active one, so the very next thing
      // they do — browsing or enrolling — is already in that child's context.
      if (student?.studentId) {
        dispatch(switchStudent(student.studentId));
      }
      navigate('/parent/dashboard', { replace: true });
    } catch (err: any) {
      setApiError(err?.message || 'No pudimos registrar al alumno. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 4 }}>Agregar alumno</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Registra a tu hijo o hija para poder inscribirlo en cursos.
      </p>

      {atLimit && (
        <div role="alert" style={alertStyle('#fff8e6', '#f5a623', '#7a4d06')}>
          Ya tienes {STUDENT_LIMITS.maxPerParent} alumnos registrados, que es el máximo por cuenta.
        </div>
      )}

      {apiError && (
        <div role="alert" style={alertStyle('#ffe9e9', '#cb4242', '#8f2323')}>
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <Field label="Nombre" htmlFor="firstName" error={errors.firstName}>
              <input
                id="firstName"
                className="form-input"
                value={form.firstName}
                onChange={(e) => set('firstName', e.target.value)}
                maxLength={STUDENT_LIMITS.nameMaxLength}
                autoComplete="off"
              />
            </Field>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <Field label="Apellidos" htmlFor="lastName" error={errors.lastName}>
              <input
                id="lastName"
                className="form-input"
                value={form.lastName}
                onChange={(e) => set('lastName', e.target.value)}
                maxLength={STUDENT_LIMITS.nameMaxLength}
                autoComplete="off"
              />
            </Field>
          </div>
        </div>

        <Field
          label="Fecha de nacimiento"
          htmlFor="birthDate"
          error={errors.birthDate}
          help={`IQMaxx es para alumnos de ${STUDENT_LIMITS.minAge} a ${STUDENT_LIMITS.maxAge} años.`}
        >
          <input
            id="birthDate"
            className="form-input"
            type="date"
            value={form.birthDate}
            onChange={(e) => set('birthDate', e.target.value)}
          />
        </Field>

        <Field label="Grado escolar" htmlFor="grade" error={errors.grade}>
          <select
            id="grade"
            className="form-input"
            value={form.grade}
            onChange={(e) => set('grade', e.target.value)}
          >
            <option value="">Elige un grado</option>
            {Array.from({ length: STUDENT_LIMITS.maxGrade }, (_, i) => i + 1).map((g) => (
              <option key={g} value={g}>
                {g}º
              </option>
            ))}
          </select>
        </Field>

        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="submit" className="btn btn-primary" disabled={saving || atLimit}>
            {saving ? 'Guardando…' : 'Agregar alumno'}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  htmlFor: string;
  error?: string;
  help?: string;
  children: React.ReactNode;
}> = ({ label, htmlFor, error, help, children }) => (
  <div className="form-group" style={{ marginBottom: 16 }}>
    <label
      htmlFor={htmlFor}
      style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}
    >
      {label}
    </label>
    {children}
    {help && !error && <div className="form-help">{help}</div>}
    {error && <div className="form-error">{error}</div>}
  </div>
);

function alertStyle(bg: string, border: string, fg: string): React.CSSProperties {
  return {
    background: bg,
    border: `1px solid ${border}`,
    color: fg,
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
    fontSize: 14,
  };
}

export default AddStudent;
